import React, { useState, useRef } from 'react';
import { useMentorAuth } from '../contexts/MentorAuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Camera, User, Mail, Save, X, Plus } from 'lucide-react';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../utils/cropImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MentorProfile() {
  const { mentor, getToken, updateMentorProfile } = useMentorAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: mentor?.name || '',
    bio: mentor?.bio || '',
    specialties: mentor?.specialties || []
  });
  
  // Photo upload states
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Specialty input
  const [newSpecialty, setNewSpecialty] = useState('');

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/mentor/me`,
        formData,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      updateMentorProfile(formData);
      toast.success('Perfil actualizado exitosamente');
      setIsEditing(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar perfil');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result);
        setShowPhotoModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleUploadPhoto = async () => {
    if (!selectedImage || !croppedAreaPixels) return;

    setIsUploading(true);
    try {
      const croppedImage = await getCroppedImg(selectedImage, croppedAreaPixels);
      
      const formData = new FormData();
      formData.append('file', croppedImage, 'profile.jpg');

      const response = await axios.post(
        `${BACKEND_URL}/api/mentor/upload-photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      updateMentorProfile({ photo_url: response.data.photo_url });
      toast.success('Foto actualizada exitosamente');
      setShowPhotoModal(false);
      setSelectedImage(null);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al subir foto');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('¿Eliminar foto de perfil?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/mentor/photo`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      updateMentorProfile({ photo_url: null });
      toast.success('Foto eliminada');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar foto');
    }
  };

  const addSpecialty = () => {
    if (newSpecialty.trim()) {
      setFormData({
        ...formData,
        specialties: [...formData.specialties, newSpecialty.trim()]
      });
      setNewSpecialty('');
    }
  };

  const removeSpecialty = (index) => {
    setFormData({
      ...formData,
      specialties: formData.specialties.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Mi Perfil</h1>
          <p className="text-gray-400">Gestiona tu información personal y profesional</p>
        </div>

        {/* Profile Card */}
        <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-8">
          {/* Photo Section */}
          <div className="flex items-center space-x-6 mb-8 pb-8 border-b border-white/10">
            <div className="relative">
              {mentor?.photo_url ? (
                <img
                  src={mentor.photo_url}
                  alt={mentor.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-teal-500"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-teal-500/20 flex items-center justify-center border-4 border-teal-500">
                  <User size={48} className="text-teal-400" />
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 p-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors"
              >
                <Camera size={20} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
              />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-1">{mentor?.name}</h2>
              <p className="text-gray-400 flex items-center gap-2">
                <Mail size={16} />
                {mentor?.email}
              </p>
              {mentor?.photo_url && (
                <button
                  onClick={handleDeletePhoto}
                  className="mt-2 text-sm text-red-400 hover:text-red-300"
                >
                  Eliminar foto
                </button>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nombre Completo
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!isEditing}
                className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none disabled:opacity-50"
              />
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Biografía
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                disabled={!isEditing}
                rows="4"
                className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none disabled:opacity-50"
                placeholder="Describe tu experiencia y especialidades..."
              />
            </div>

            {/* Specialties */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Especialidades
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.specialties.map((specialty, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-teal-500/20 text-teal-400 rounded-full text-sm flex items-center gap-2"
                  >
                    {specialty}
                    {isEditing && (
                      <button
                        onClick={() => removeSpecialty(index)}
                        className="hover:text-red-400"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {isEditing && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSpecialty}
                    onChange={(e) => setNewSpecialty(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addSpecialty()}
                    placeholder="Agregar especialidad..."
                    className="flex-1 px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none"
                  />
                  <button
                    onClick={addSpecialty}
                    className="px-4 py-2 bg-teal-500/20 text-teal-400 rounded-lg hover:bg-teal-500/30 transition-colors"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium"
                >
                  Editar Perfil
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        name: mentor?.name || '',
                        bio: mentor?.bio || '',
                        specialties: mentor?.specialties || []
                      });
                    }}
                    className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                    className="px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
                  >
                    <Save size={20} />
                    {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Photo Crop Modal */}
      {showPhotoModal && selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90">
          <div className="bg-black/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 max-w-2xl w-full">
            <h3 className="text-xl font-bold text-white mb-4">Ajustar Foto</h3>
            
            <div className="relative h-96 bg-black rounded-lg mb-4">
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Zoom</label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowPhotoModal(false);
                  setSelectedImage(null);
                }}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
              >
                Cancelar
              </button>
              <button
                onClick={handleUploadPhoto}
                disabled={isUploading}
                className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
              >
                {isUploading ? 'Subiendo...' : 'Guardar Foto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
